import React from 'react';
import { Link } from 'react-router-dom';
import { useCart, formatPrice, parsePrice } from '../context/CartContext';
import { useT, useLanguage } from '../i18n/LanguageContext';
import { TYPE_KEYS } from '../i18n/translations';

/* ============================================================
   CartPreview — dropdown no hover do ícone do carrinho
   Mostra até 3 artigos, subtotal e CTAs para carrinho/checkout.
   ============================================================ */

const MAX_VISIBLE = 3;

export default function CartPreview({ open, onMouseEnter, onMouseLeave, onClose }) {
  const t = useT();
  const { lang } = useLanguage();
  const { items, totalItems, subtotal, subtotalFormatted, removeItem, updateQty } = useCart();

  const localeForPrice = lang === 'en' ? 'en-GB' : 'pt-PT';
  const visible = items.slice(0, MAX_VISIBLE);
  const extra   = items.length - MAX_VISIBLE;

  return (
    <div
      className={`cart-preview${open ? ' cart-preview--open' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-hidden={!open}
      role="region"
      aria-label={t('cart.eyebrow')}
    >
      {/* Triângulo que aponta para o ícone do carrinho */}
      <div className="cart-preview__caret" aria-hidden="true" />

      {items.length === 0 ? (
        /* ── Estado vazio ── */
        <div className="cart-preview__empty">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--clr-ouro)" strokeWidth="1.2" aria-hidden="true">
            <path d="M5 7 H19 L17.5 17 H6.5 L5 7 Z" />
            <path d="M8 7 C8 4.8 9.8 3 12 3 C14.2 3 16 4.8 16 7" />
          </svg>
          <p className="t-small" style={{ marginTop: 'var(--sp-3)', color: 'var(--clr-noir)', opacity: 0.55 }}>
            {t('cart.emptyTitle')}
          </p>
          <Link to="/loja" className="btn btn--compact" onClick={onClose} style={{ marginTop: 'var(--sp-4)' }}>
            {t('cart.emptyCta')}
          </Link>
        </div>
      ) : (
        <>
          {/* ── Lista de artigos ── */}
          <ul className="cart-preview__list" role="list">
            {visible.map(item => {
              const typeLabel = TYPE_KEYS[item.type] ? t(TYPE_KEYS[item.type]) : item.type;
              const lineTotal = parsePrice(item.price) * item.qty;
              return (
                <li key={item.id} className="cart-preview__item">
                  <button
                    type="button"
                    className="cart-preview__remove"
                    onClick={() => removeItem(item.id)}
                    aria-label={`${t('cart.remove')} ${item.name}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                  <div className="cart-preview__img">
                    {item.image
                      ? <img src={item.image} alt={item.name} />
                      : <svg width="14" height="28" viewBox="0 0 16 32" fill="none" aria-hidden="true"><path d="M8 1 C4 5 2 10 2 14 C2 20 5 24 8 25 C11 24 14 20 14 14 C14 10 12 5 8 1Z" stroke="var(--clr-ouro)" strokeWidth="1" fill="none" /><line x1="8" y1="25" x2="8" y2="31" stroke="var(--clr-ouro)" strokeWidth="1" /></svg>
                    }
                  </div>
                  <div className="cart-preview__info">
                    <p className="cart-preview__name">{item.name}</p>
                    <p className="cart-preview__meta">{typeLabel} · {item.year}</p>
                    <div className="cart-preview__controls">
                      <button
                        type="button"
                        className="cart-preview__qty-btn"
                        onClick={() => updateQty(item.id, item.qty - 1)}
                        aria-label={t('cart.qtyDecrease')}
                      >−</button>
                      <span className="cart-preview__qty">{item.qty}</span>
                      <button
                        type="button"
                        className="cart-preview__qty-btn"
                        onClick={() => updateQty(item.id, item.qty + 1)}
                        aria-label={t('cart.qtyIncrease')}
                      >+</button>
                    </div>
                  </div>
                  <span className="cart-preview__price">{formatPrice(lineTotal, localeForPrice)}</span>
                </li>
              );
            })}
            {extra > 0 && (
              <li className="cart-preview__extra">
                + {extra} {extra === 1 ? t('cart.itemSingular') : t('cart.itemPlural')}
              </li>
            )}
          </ul>

          {/* ── Divisor ── */}
          <div className="divider" style={{ margin: 'var(--sp-4) 0 var(--sp-3)' }} />

          {/* ── Footer ── */}
          <div className="cart-preview__footer">
            <div className="cart-preview__subtotal">
              <span className="t-small">{t('cart.subtotal')}</span>
              <span className="t-small" style={{ fontWeight: 'var(--fw-semi)' }}>{subtotalFormatted}</span>
            </div>
            <Link
              to="/checkout"
              className="btn btn--compact"
              onClick={onClose}
              style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--sp-3)' }}
            >
              {t('cart.checkout')}
            </Link>
            <Link
              to="/carrinho"
              className="cart-preview__view-cart"
              onClick={onClose}
            >
              {t('cart.title')} ({totalItems})
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
